import { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'

const POLL_INTERVAL = 15 * 60 * 1000 // 15 minutes
const INITIAL_DELAY = 60 * 1000 // Wait 1 minute after mount before first check

export function useVersionCheck() {
  const session = useSelector(state => state.auth.session)
  const isAuthenticated = !!session

  const [hasUpdate, setHasUpdate] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const lastCheckRef = useRef(Date.now())
  const intervalRef = useRef(null)
  const initialTimeoutRef = useRef(null)
  const currentVersionRef = useRef(null)
  const abortControllerRef = useRef(null)
  const isCheckingRef = useRef(false)

  const checkVersion = useCallback(async () => {
    // Duplicate prevention
    if (!isAuthenticated || isCheckingRef.current) return

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    isCheckingRef.current = true

    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
        signal: abortControllerRef.current.signal
      })

      // Check if aborted before processing
      if (abortControllerRef.current.signal.aborted) return

      if (!response.ok) return

      const data = await response.json()

      // Check if aborted before state updates
      if (abortControllerRef.current.signal.aborted) return

      lastCheckRef.current = Date.now()

      // Cache initial version on first successful fetch
      if (!currentVersionRef.current) {
        currentVersionRef.current = data.version
        return
      }

      // Compare versions
      if (data.version !== currentVersionRef.current) {
        setHasUpdate(true)
        setIsDismissed(false)
      }
    } catch (error) {
      // Ignore abort errors, warn on others
      if (error.name !== 'AbortError') {
        console.warn('[VersionCheck] Failed to fetch version:', error.message)
      }
    } finally {
      isCheckingRef.current = false
    }
  }, [isAuthenticated])

  // Visibility change handler - check if stale when tab becomes visible
  useEffect(() => {
    if (!isAuthenticated) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastCheck = Date.now() - lastCheckRef.current
        if (timeSinceLastCheck >= POLL_INTERVAL) {
          checkVersion()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isAuthenticated, checkVersion])

  // Polling interval (only checks when visible)
  useEffect(() => {
    if (!isAuthenticated) return

    // Initial check after delay
    initialTimeoutRef.current = setTimeout(checkVersion, INITIAL_DELAY)

    // Set up interval
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkVersion()
      }
    }, POLL_INTERVAL)

    return () => {
      clearTimeout(initialTimeoutRef.current)
      clearInterval(intervalRef.current)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [isAuthenticated, checkVersion])

  const dismiss = useCallback(() => {
    setIsDismissed(true)
  }, [])

  const reload = useCallback(() => {
    window.location.reload()
  }, [])

  return {
    hasUpdate: hasUpdate && !isDismissed,
    dismiss,
    reload
  }
}
